import type {
  AutonomousApplicationGrant,
  ExternalAction,
  ExternalActionReceipt,
  JobApplication,
} from './model.js'

export type AuthorizationDecision =
  | { readonly allowed: true; readonly grant: AutonomousApplicationGrant; readonly idempotencyKey: string }
  | { readonly allowed: false; readonly reason: string }

function isWithinWindow(grant: AutonomousApplicationGrant, now: string): boolean {
  const timestamp = Date.parse(now)
  return timestamp >= Date.parse(grant.validFrom) && timestamp <= Date.parse(grant.validUntil)
}

export function executionIdempotencyKey(
  applicationId: string,
  action: ExternalAction,
  resumeId: string | null,
): string {
  return `offerflow:${applicationId}:${action}:${resumeId ?? 'none'}`
}

export function authorizeExternalAction(input: {
  readonly application: JobApplication
  readonly action: ExternalAction
  readonly additionalActions?: readonly ExternalAction[]
  readonly channel: string
  readonly grants: readonly AutonomousApplicationGrant[]
  readonly receipts: readonly ExternalActionReceipt[]
  readonly now: string
}): AuthorizationDecision {
  const resumeId = input.application.selectedResumeId
  if (resumeId === null && input.action === 'upload_resume') {
    return { allowed: false, reason: '申请尚未选择简历' }
  }

  const key = executionIdempotencyKey(input.application.id, input.action, resumeId)
  const completed = input.receipts.find(receipt =>
    receipt.idempotencyKey === key && receipt.outcome === 'completed')
  if (completed !== undefined) {
    return { allowed: false, reason: '该外部动作已经完成，已通过幂等回执阻止重复执行' }
  }

  for (const grant of [...input.grants].reverse()) {
    if (grant.revokedAt !== null) continue
    if (!isWithinWindow(grant, input.now)) continue
    if (!grant.applicationIds.includes(input.application.id)) continue
    const requiredActions = [input.action, ...(input.additionalActions ?? [])]
    if (!requiredActions.every(action => grant.actions.includes(action))) continue
    if (!grant.channels.includes(input.channel)) continue
    if (resumeId !== null && !grant.resumeIds.includes(resumeId)) continue
    const used = input.receipts.filter(receipt =>
      receipt.grantId === grant.id && receipt.outcome === 'completed').length
    if (used >= grant.volumeLimit) continue
    return { allowed: true, grant, idempotencyKey: key }
  }

  return { allowed: false, reason: '没有覆盖当前申请、简历、渠道、动作和时间范围的有效授权' }
}

export function applicationStatusLabel(status: JobApplication['status']): string {
  switch (status) {
    case 'waiting_authorization': return '等待授权'
    case 'ready': return '已就绪'
    case 'running': return '自动执行中'
    case 'paused': return '已暂停'
    case 'needs_attention': return '需处理'
    case 'submitted': return '已完成并保存回执'
  }
}
